import * as Actions from 'actions'
import { isEmpty } from 'lodash'
import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHotkeys } from 'react-hotkeys-hook'

import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import { Box, ListItemText } from '@material-ui/core'
import { ReactComponent as BrushIcon } from '~/assets/media/ic_brush.svg'
import { ReactComponent as FiltersIcon } from '~/assets/media/ic_filtres.svg'
import { ReactComponent as ImagesIcon } from '~/assets/media/ic_images.svg'
import { ReactComponent as MediaIcon } from '~/assets/media/ic_media.svg'
import { ReactComponent as SoundIcon } from '~/assets/media/ic_sound.svg'
import { ReactComponent as TextIcon } from '~/assets/media/ic_text.svg'
import { ReactComponent as TransitionIcon } from '~/assets/media/ic_transitions.svg'
import { ReactComponent as VideosIcon } from '~/assets/media/ic_videos.svg'

import 'styles/style.scss'
import './Menu.scss'

import { MENU_ITEMS, SOURCE_FILE_TYPES, HOTKEYS } from '~/enums'
import { useAction } from '~/hooks'

import Scrollbars from '~/components/base/Scrollbars'
import { TranslationContext } from '~/contexts/TranslationContext'
import { selectSourceFilesParams } from '~/selectors'
import { EditorMenu } from '~/components/EditorMenu/EditorMenu'
import { Orientation } from '~/types/userInterface'
import { TransformUIDropContainer } from '~/components/TransformUIDropContainer'
import { DROP_CONTAINER } from '~/config/constants/ui'
import { selectViewSettings } from '~/selectors/mainView'
import { activeHotkeyProfileSelector } from '~/selectors/user-profile-selector'
import { setEditorSettings } from '~/actions/mainView'
import { EDITOR_MAIN_SETTINGS } from '~/config/constants/mainView'
import { ViewSettings } from '~/config/constants/editorSettings'
import { getEnabledMenuItems, getDefaultChapterFromStorage } from '~/general'


function MenuIcon({ type }) {
  switch (type) {
    case MENU_ITEMS.MEDIA:
      return <MediaIcon />
    case MENU_ITEMS.PROJECT_MEDIA:
      return <MediaIcon />
    case MENU_ITEMS.TRANSITIONS:
      return <TransitionIcon />
    case MENU_ITEMS.TEXT:
      return <TextIcon />
    case MENU_ITEMS.AUDIO:
      return <SoundIcon />
    case MENU_ITEMS.FILTERS:
      return <FiltersIcon />
    case MENU_ITEMS.DESIGN:
      return <BrushIcon />
    case MENU_ITEMS.IMAGES:
      return <ImagesIcon />
    case MENU_ITEMS.VIDEOS:
      return <VideosIcon />
    default:
      throw new Error(`Can't find ${type} icon`)
  }
}

function getMenuItemClassName(type) {
  switch (type) {
    case MENU_ITEMS.MEDIA:
      return 'menu-item__media'
    case MENU_ITEMS.PROJECT_MEDIA:
      return 'menu-item__media'
    case MENU_ITEMS.TRANSITIONS:
      return 'menu-item__transitions'
    case MENU_ITEMS.TEXT:
      return 'menu-item__textpanel'
    case MENU_ITEMS.AUDIO:
      return 'menu-item__audiopanel'
    case MENU_ITEMS.FILTERS:
      return 'menu-item__filters'
    case MENU_ITEMS.DESIGN:
      return 'menu-item__design'
    case MENU_ITEMS.IMAGES:
      return 'menu-item__images'
    case MENU_ITEMS.VIDEOS:
      return 'menu-item__videos'
    default:
      throw new Error(`Can't find ${type} className`)
  }
}

function WebEditorMenu() {
  const openMenuChapter = useAction(Actions.mainView.openChapter)
  const onSetEditorSettings = useAction(setEditorSettings)
  const { menuItem } = useSelector(state => state.mainView)
  const settings = useSelector(selectViewSettings)
  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const showProjectMedia = settings[ViewSettings.showProjectMedia]
  const showMyMedia = settings[ViewSettings.showMyMedia]

  // issue 3189: don't show images item in the menu if lib sources does not exist
  const { imageLibSources } = useSelector(state => selectSourceFilesParams(state,
    SOURCE_FILE_TYPES.IMAGES))
  const enabledIcons = useMemo(() => getEnabledMenuItems().filter(({ menuItem, visible }) => {
    if (menuItem === MENU_ITEMS.IMAGES) {
      return !isEmpty(imageLibSources)
    }
    if (menuItem === MENU_ITEMS.PROJECT_MEDIA) {
      return !!showProjectMedia
    }
    if (menuItem === MENU_ITEMS.MEDIA) {
      return !!showMyMedia
    }
    return visible
  }),
  [ imageLibSources, showProjectMedia, showMyMedia ])

  const { t } = useContext(TranslationContext)

  function MenuText(type) {
    switch (type) {
      case MENU_ITEMS.MEDIA:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_MY_MEDIA',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_MY_MEDIA'),
          title: t('LEFT_TOOLBAR_MENU_BTN_MY_MEDIA_TOOLTIP'),
        }
      case MENU_ITEMS.PROJECT_MEDIA:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_PROJECT_MEDIA',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_PROJECT_MEDIA'),
          title: t('LEFT_TOOLBAR_MENU_BTN_PROJECT_MEDIA_TOOLTIP'),
        }
      case MENU_ITEMS.TRANSITIONS:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_TRANSITIONS',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_TRANSITIONS'),
          title: t('LEFT_TOOLBAR_MENU_BTN_TRANSITIONS_TOOLTIP'),
        }
      case MENU_ITEMS.IMAGES:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_IMAGES',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_IMAGES'),
          title: t('LEFT_TOOLBAR_MENU_BTN_IMAGES_TOOLTIP'),
        }
      case MENU_ITEMS.TEXT:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_TEXT',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_TEXT'),
          title: t('LEFT_TOOLBAR_MENU_BTN_TEXT_TOOLTIP'),
        }
      case MENU_ITEMS.AUDIO:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_AUDIO',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_AUDIO'),
          title: t('LEFT_TOOLBAR_MENU_BTN_AUDIO_TOOLTIP'),
        }
      case MENU_ITEMS.FILTERS:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_FILTERS',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_FILTERS'),
          title: t('LEFT_TOOLBAR_MENU_BTN_FILTERS_TOOLTIP'),
        }
      case MENU_ITEMS.DESIGN:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_DESIGN',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_DESIGN'),
          title: t('LEFT_TOOLBAR_MENU_BTN_DESIGN_TOOLTIP'),
        }
      case MENU_ITEMS.VIDEOS:
        return {
          dataLangId: 'LEFT_TOOLBAR_MENU_BTN_VIDEOS',
          menuText: t('LEFT_TOOLBAR_MENU_BTN_VIDEOS'),
          title: t('LEFT_TOOLBAR_MENU_BTN_VIDEOS_TOOLTIP'),
        }
      default:
        return {
          dataLangId: t(type?.toUpperCase()),
          menuText: t(type?.toUpperCase()),
          title: t(type?.toUpperCase()),
        }
    }
  }

  const customizationView = useSelector(state => state.mainView.showCustomizeView)
  const toggleMyMedia = useCallback(e => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    onSetEditorSettings(EDITOR_MAIN_SETTINGS.VIEW, {
      ...settings,
      [ViewSettings.showMyMedia]: !showMyMedia,
    })
    openMenuChapter(getDefaultChapterFromStorage(), true)
  }, [ onSetEditorSettings, settings, showMyMedia, openMenuChapter ])
  const showSelected = !customizationView

  useEffect(() => {
    if (!showMyMedia || !showProjectMedia) {
      openMenuChapter(getDefaultChapterFromStorage(), true)
    }
  }, [ showMyMedia, showProjectMedia, enabledIcons, openMenuChapter ])

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.TOGGLE_MY_MEDIA_VISIBLE], toggleMyMedia, {
    enableOnFormTags: true,
    preventDefault: true,
  })
  return (
    <div className="menu-tabs">
      <TransformUIDropContainer
        position="fixed"
        orientation={Orientation.Vertical}
        dropContainer={DROP_CONTAINER.LEFT_MENU}
      />
      <EditorMenu />
      <Scrollbars trackHorizontalSize={0} trackSize={6} className="menu-tabs__scrollbar">
        <Box disablePadding>
          {enabledIcons.map(({ menuItem: iconType }) => {
            const currentIcon = MenuText(iconType)
            return (
              <ListItem
                key={iconType}
                button
                disableGutters
                selected={menuItem === iconType && showSelected}
                onClick={() => openMenuChapter(iconType, true)}
                className={getMenuItemClassName(iconType)}
                data-lang-id={currentIcon.dataLangId}
                title={currentIcon.title}
              >
                <ListItemIcon>
                  <MenuIcon type={iconType} />
                </ListItemIcon>
                <ListItemText primary={currentIcon.menuText} />
              </ListItem>
            )
          })}
        </Box>
      </Scrollbars>
    </div>
  )
}

export default memo(WebEditorMenu)
